import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Modal,
  Box,
  Alert,
  TextField,
  Stack,
  InputAdornment,
  useTheme,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Group as GroupIcon,
  Add as AddIcon,
  Search as SearchIcon,
  KeyboardArrowRight as ArrowRightIcon,
  KeyboardArrowDown as ArrowDownIcon,
  ArrowBackIos as ArrowBackIosIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import axios from "axios";

import {
  fetchAppelOffresForCurrentUser,
  fetchAppelOffreById,
  removeAppelOffre,
  searchAppelOffresByTitle,
} from "../../redux/appeloffreSlice";

import AddAppelOffreForm from "./AddAppelOffreForm";
import ModifyAppelOffreModal from "./ModifyAppelOffreModal";
import DeleteModal from "../Common/DeleteModal";
import ProfileCard from "../Profiles/ProfileCard";
import ProfileDetails from "../Profiles/ProfileDetails";
import CustomLoader from "../Common/CustomLoader";

import "./styles/AppelOffreManagement.css";

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                   */
/* -------------------------------------------------------------------------- */
const isExpired = (d) => dayjs(d).isBefore(dayjs(), "day");

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "#fff",
  boxShadow: 24,
  borderRadius: 3,
};

export default function AppelOffreManagement() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { appelOffres, loading, selectedAppelOffre, error } = useSelector(
    (s) => s.appelOffres
  );

  /* ----------------------------- local state ------------------------------ */
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [editSuccessMessage, setEditSuccessMessage] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteAppelOffreId, setDeleteAppelOffreId] = useState(null);
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedContent, setExpandedContent] = useState({});
  const [searchKeyword, setSearchKeyword] = useState("");

  /* pagination perso (10 lignes) */
  const PER = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const total = Math.ceil(appelOffres.length / PER);
  const pageSlice = appelOffres.slice(
    (currentPage - 1) * PER,
    currentPage * PER
  );

  /* Candidatures */
  const [openCandidaturesModal, setOpenCandidaturesModal] = useState(false);
  const [candidatures, setCandidatures] = useState([]);
  const [candidaturesPage, setCandidaturesPage] = useState(1);
  const [selectedProfile, setSelectedProfile] = useState(null);

  /* --- NEW : confirm / search‑results dialog --- */
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [autoSearchEnabled, setAutoSearchEnabled] = useState(false);
  const [profilesFetched, setProfilesFetched] = useState(false);
  const [encodedDesc, setEncodedDesc] = useState("");

  /* ------------------------------ lifecycle ------------------------------- */
  useEffect(() => {
    dispatch(fetchAppelOffresForCurrentUser());
  }, [dispatch]);

  /* ------------------------------- handlers ------------------------------- */
  const toggleRow = (id) =>
    setExpandedRowId((prev) => (prev === id ? null : id));

  const toggleContent = (field, id) =>
    setExpandedContent((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: !prev[id]?.[field] },
    }));

  const handleOpenAddModal = () => setOpenAddModal(true);
  const handleCloseAddModal = () => setOpenAddModal(false);

  /* -------- ADD success -> open confirm dialog -------- */
  const handleAddSuccess = (newAppelOffre, autoSearch = false) => {
    setEditSuccessMessage("Ajout réussi");
    setAutoSearchEnabled(autoSearch);

    const combinedDescription = `
      ${newAppelOffre.commentaire || ""}
      ${newAppelOffre.titre || ""}
      ${newAppelOffre.descriptif || ""}
      ${newAppelOffre.annee_experiences || ""}
      ${newAppelOffre.tjm || ""}
      ${newAppelOffre.lieu_prestation || ""}
    `;
    setEncodedDesc(encodeURIComponent(combinedDescription));

    if (autoSearch) setProfilesFetched(true);

    setOpenConfirmDialog(true);
    setTimeout(() => setEditSuccessMessage(""), 3000);
  };

  const handleOpenEditModal = async (id) => {
    await dispatch(fetchAppelOffreById(id));
    setOpenEditModal(true);
  };
  const handleCloseEditModal = () => setOpenEditModal(false);

  const handleDelete = (id) => {
    dispatch(removeAppelOffre(id)).then(() => {
      setSuccessMessage("Suppression effectuée");
      setTimeout(() => setSuccessMessage(""), 3000);
      dispatch(fetchAppelOffresForCurrentUser());
    });
  };

  const openDeleteModal = (id) => {
    setDeleteAppelOffreId(id);
    setDeleteModalOpen(true);
  };
  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteAppelOffreId(null);
  };
  const confirmDelete = async () => {
    if (deleteAppelOffreId) {
      await handleDelete(deleteAppelOffreId);
      closeDeleteModal();
    }
  };

  const handleSearch = () => dispatch(searchAppelOffresByTitle(searchKeyword));

  /* ---- NEW confirm dialog handlers ---- */
  const handleFindCandidates = () => setProfilesFetched(true);
  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setProfilesFetched(false);
  };

  /* ---- Candidatures ---- */
  const handleOpenCandidaturesModal = async (id) => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/appelOffres/${id}/candidatures`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );
      setCandidatures(res.data.candidatures || []);
      setOpenCandidaturesModal(true);
    } catch (err) {
      console.error("Erreur récupération candidatures :", err);
      setCandidatures([]);
      setOpenCandidaturesModal(true);
    }
  };
  const handleCloseCandidaturesModal = () => {
    setOpenCandidaturesModal(false);
    setCandidatures([]);
    setCandidaturesPage(1);
  };

  const candidaturesPageTo = (p) => {
    const PER_PAGE = 2;
    const totalPages = Math.ceil(candidatures.length / PER_PAGE);
    if (p > 0 && p <= totalPages) {
      setCandidaturesPage(p);
    }
  };

  const handleProfileClick = (profile) => {
    setSelectedProfile(profile);
  };
  const handleCloseProfileDetails = () => setSelectedProfile(null);

  /* ------------------------------- loading ------------------------------- */
  if (loading) {
    return (
      <Container className="loading-screen">
        <CustomLoader />
      </Container>
    );
  }

  /* -------------------------------- render ------------------------------- */
  return (
    <Container className="appeloffre-container">
      <Typography
        variant="h4"
        className="appeloffre-title"
        sx={{ mb: 4, fontWeight: "bold", color: "#2F4E8B", textAlign: "center" }}
      >
        Gestion des Appels d’Offre
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {editSuccessMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {editSuccessMessage}
        </Alert>
      )}

      {/* search & add */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        sx={{ flexWrap: "wrap", mb: 3, gap: 2 }}
      >
        <TextField
          placeholder="Rechercher un appel d'offre"
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          onKeyPress={(e) => e.key === "Enter" && handleSearch()}
          size="small"
          className="search-bar"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#2F4E8B" }} />
              </InputAdornment>
            ),
            sx: {
              borderRadius: 3,
              "& fieldset": { borderColor: "#2F4E8B !important" },
              "&:hover fieldset": { borderColor: "#2F4E8B !important" },
              "&.Mui-focused fieldset": { borderColor: "#2F4E8B !important" },
              "&::-webkit-scrollbar": {
                width: "8px",
                height: "8px", // Added for horizontal scrollbar
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#2F4E8B", // Main blue color
                borderRadius: "4px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#0d1b3e", // Darker blue on hover
              },
              "&::-webkit-scrollbar-thumb:horizontal": { // Horizontal scrollbar
                backgroundColor: "#2F4E8B", // Main blue color
              },
              "&::-webkit-scrollbar-thumb:horizontal:hover": {
                backgroundColor: "#0d1b3e", // Darker blue on hover
              },
            },
          }}
        />

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddModal}
          className="add-btn"
          sx={{
            textTransform: "none",
            bgcolor: "#2F4E8B",
            "&:hover": { bgcolor: "#4a6bad" },
            fontWeight: "bold",
            py: 1,
            px: 3,
            borderRadius: "50px",
            color: "#fff",
          }}
        >
          Ajouter
        </Button>
      </Stack>

      {/* table */}
      <TableContainer
        component={Paper}
        className="table-wrapper"
        sx={{
          "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px", // Added for horizontal scrollbar
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#2F4E8B", // Main blue color
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#0d1b3e", // Darker blue on hover
          },
          "&::-webkit-scrollbar-thumb:horizontal": { // Horizontal scrollbar
            backgroundColor: "#2F4E8B", // Main blue color
          },
          "&::-webkit-scrollbar-thumb:horizontal:hover": {
            backgroundColor: "#0d1b3e", // Darker blue on hover
          },
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow className="table-head-row">
              <TableCell width={44} />
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Titre
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Référence
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Date de publication
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Date de Cloture
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Lieu
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}>
                Mode
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", fontSize: "1rem", color: "#2F4E8B" }}
              >
                Action
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {pageSlice.map((row) => {
              const expanded = expandedRowId === row.id;
              return (
                <React.Fragment key={row.id}>
                  <TableRow
                    hover
                    className={`table-body-row ${expanded ? "expanded" : ""}`}
                    sx={{
                      "& .MuiTableCell-root": {
                        fontSize: "0.9rem",
                        color: "#4a4a4a",
                      },
                    }}
                  >
                    {/* Arrow cell */}
                    <TableCell width={44}>
                      <IconButton
                        size="small"
                        onClick={() => toggleRow(row.id)}
                        className={`arrow-btn ${expanded ? "open" : ""}`}
                      >
                        {expanded ? (
                          <ArrowDownIcon fontSize="inherit" />
                        ) : (
                          <ArrowRightIcon fontSize="inherit" />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body1" sx={{ fontWeight: 560 }}>
                        {row.titre}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.reference}</TableCell>
                    <TableCell>{row.date_publication}</TableCell>
                    <TableCell
                      sx={{
                        color: isExpired(row.date_cloture_offre)
                          ? "#ff4d4f"
                          : "#22c55e",
                      }}
                    >
                      {row.date_cloture_offre}
                    </TableCell>
                    <TableCell>{row.lieu_prestation}</TableCell>
                    <TableCell>{row.mode}</TableCell>

                    {/* action icons */}
                    <TableCell align="center">
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="center"
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleOpenCandidaturesModal(row.id)}
                          className="action-icon"
                        >
                          <GroupIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEditModal(row.id)}
                          className="action-icon"
                        >
                          <EditIcon fontSize="inherit" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteModal(row.id)}
                          className="action-icon"
                        >
                          <DeleteIcon
                            fontSize="inherit"
                            sx={{ color: theme.palette.error.main }}
                          />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  {/* details row */}
                 {expanded && (
  <TableRow className="details-row">
    <TableCell colSpan={8}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 3,
          pt: 2,
        }}
      >
        {/* Row 1 */}
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            Client Final
          </Typography>
          <Typography variant="body2">
            {row.client_final || "—"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            Référence Client
          </Typography>
          <Typography variant="body2">
            {row.reference_client || "—"}
          </Typography>
        </Box>

        {/* Row 2 */}
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            Année d'Expériences
          </Typography>
          <Typography variant="body2">
            {row.annee_experiences || "—"}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            TJM
          </Typography>
          <Typography variant="body2">
            {row.tjm || "—"}
          </Typography>
        </Box>

        {/* Row 3 */}
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            Descriptif
          </Typography>
          <Typography variant="body2">
            {expandedContent[row.id]?.descriptif
              ? row.descriptif
              : `${row.descriptif?.slice(0, 100) || "—"}${
                  row.descriptif?.length > 100 ? "..." : ""
                }`}
            {row.descriptif?.length > 100 && (
              <Button
                size="small"
                onClick={() =>
                  toggleContent("descriptif", row.id)
                }
                sx={{ textTransform: "none", ml: 1, color: "#2F4E8B", fontWeight: "bold" }}
              >
                {expandedContent[row.id]?.descriptif ? "Voir moins" : "Voir plus"}
              </Button>
            )}
          </Typography>
        </Box>
        <Box>
          <Typography variant="subtitle2" sx={{ color: "#9ca3af", fontWeight: 'bold' }}>
            Commentaire
          </Typography>
          <Typography variant="body2">
            {expandedContent[row.id]?.commentaire
              ? row.commentaire
              : `${row.commentaire?.slice(0, 100) || "—"}${
                  row.commentaire?.length > 100 ? "..." : ""
                }`}
            {row.commentaire?.length > 100 && (
              <Button
                size="small"
                onClick={() =>
                  toggleContent("commentaire", row.id)
                }
                sx={{ textTransform: "none", ml: 1, color: "#2F4E8B", fontWeight: "bold" }}
              >
                {expandedContent[row.id]?.commentaire ? "Voir moins" : "Voir plus"}
              </Button>
            )}
          </Typography>
        </Box>
      </Box>
    </TableCell>
  </TableRow>
)}

                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom Pagination */}
      {appelOffres.length > PER && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          mt={4}
          gap={2}
        >
          <IconButton
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ArrowBackIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              bgcolor: "#2F4E8B",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: 700,
            }}
          >
            {currentPage}
          </Box>
          <IconButton
            onClick={() => setCurrentPage((p) => Math.min(total, p + 1))}
            disabled={currentPage === total}
          >
            <ArrowForwardIosIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>
        </Box>
      )}

      {/* ADD MODAL --------------------------------------------------------- */}
      <Modal open={openAddModal} onClose={null}>
        <Box sx={modalStyle}>
          <AddAppelOffreForm
            handleClose={handleCloseAddModal}
            onSuccess={handleAddSuccess}
          />
        </Box>
      </Modal>

      {/* EDIT MODAL -------------------------------------------------------- */}
      <Modal open={openEditModal} onClose={null}>
        <Box sx={modalStyle}>
          <ModifyAppelOffreModal
            open={openEditModal}
            handleClose={handleCloseEditModal}
            appelOffre={selectedAppelOffre}
            onSuccess={() => {
              setEditSuccessMessage("Appel d'offre modifié avec succès");
              setTimeout(() => setEditSuccessMessage(""), 3000);
            }}
          />
        </Box>
      </Modal>

       {/* ---- NEW CONFIRM / SEARCH RESULTS MODAL ---- */}
      <Modal open={openConfirmDialog} onClose={null}>
        <Box
          sx={{
            ...modalStyle,
            width: { xs: "90%", sm: 520 },
            p: 4,
            textAlign: "center",
            position: "relative",
          }}
        >
          {/* Icône Close en haut à droite */}
          <IconButton
            size="small"
            onClick={handleCloseConfirmDialog}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <CloseIcon sx={{ color: "#2F4E8B" }} />
          </IconButton>

          <Typography
            variant="h5"
            sx={{ fontWeight: "bold", color: "#2F4E8B", mb: 2 }}
          >
            {autoSearchEnabled || profilesFetched
              ? "Résultats de la recherche"
              : "Confirmation"}
          </Typography>

          {autoSearchEnabled || profilesFetched ? (
            <>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", mb: 3, color: "#333" }}
              >
                Cliquez ci-dessous pour voir les profils trouvés :
              </Typography>
              <Button
                variant="contained"
                sx={{
                  mb: 3,
                  backgroundColor: "#2F4E8B",
                  "&:hover": { backgroundColor: "#4a6bad" },
                  fontWeight: "bold",
                  borderRadius: "30px",
                  px: 4,
                }}
                onClick={() => {
                  const win = window.open(
                    `/appeloffre-results?desc=${encodedDesc}`,
                    "_blank",
                    "noopener,noreferrer"
                  );
                  if (win) win.focus();
                }}
              >
                Voir les profils
              </Button>
            </>
          ) : (
            <>
              <Typography
                variant="body1"
                sx={{ fontWeight: "bold", mb: 3, color: "#333" }}
              >
                Voulez-vous rechercher des candidats basés sur l'appel d'offre
                ajouté ?
              </Typography>

              <Stack direction="row" spacing={2} justifyContent="center">
                <Button
                  variant="outlined"
                  sx={{
                    fontWeight: "bold",
                    borderColor: "#2F4E8B",
                    color: "#2F4E8B",
                    borderRadius: "30px",
                    px: 4,
                  }}
                  onClick={handleCloseConfirmDialog}
                >
                  Non
                </Button>
                <Button
                  variant="contained"
                  sx={{
                    fontWeight: "bold",
                    backgroundColor: "#2F4E8B",
                    "&:hover": { backgroundColor: "#4a6bad" },
                    borderRadius: "30px",
                    px: 4,
                  }}
                  onClick={handleFindCandidates}
                >
                  Oui
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Modal>


      {/* CANDIDATURES MODAL ----------------------------------------------- */}
      <Modal open={openCandidaturesModal} onClose={null}>
        <Box
          sx={{
            ...modalStyle,
            maxWidth: 1400,
            maxHeight: "90vh",
            overflowY: "auto",
            p: 5,
            borderRadius: 3,
            bgcolor: "#fff",
            boxShadow: 24,
          }}
        >
          {/* Close icon */}
          <Box sx={{ position: "absolute", top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={handleCloseCandidaturesModal}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedProfile ? (
            <Box>
              <IconButton
                size="small"
                onClick={handleCloseProfileDetails}
                sx={{ mb: 2 }}
              >
                <ArrowBackIosIcon />
              </IconButton>
              <ProfileDetails profile={selectedProfile} />
            </Box>
          ) : (
            <>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: "bold",
                  color: "#2F4E8B",
                  mb: 4,
                  textAlign: "center",
                }}
              >
                Candidatures
              </Typography>

              {candidatures.length > 0 ? (
                <>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: 4,
                    }}
                  >
                    {candidatures
                      .slice((candidaturesPage - 1) * 2, candidaturesPage * 2)
                      .map((c) => (
                        <Box
                          key={c.id}
                          onClick={() => handleProfileClick(c.profile)}
                          sx={{
                            cursor: "pointer",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            },
                          }}
                        >
                          <ProfileCard profile={c.profile} />
                        </Box>
                      ))}
                  </Box>

                  {/* Pagination UI */}
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    mt={4}
                    gap={2}
                  >
                    <IconButton
                      onClick={() =>
                        candidaturesPageTo(candidaturesPage - 1)
                      }
                      disabled={candidaturesPage === 1}
                    >
                      <ArrowBackIosIcon sx={{ color: "#2F4E8B" }} />
                    </IconButton>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "#2F4E8B",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                      }}
                    >
                      {candidaturesPage}
                    </Box>
                    <IconButton
                      onClick={() =>
                        candidaturesPageTo(candidaturesPage + 1)
                      }
                      disabled={
                        candidaturesPage === Math.ceil(candidatures.length / 2)
                      }
                    >
                      <ArrowForwardIosIcon sx={{ color: "#2F4E8B" }} />
                    </IconButton>
                  </Box>
                </>
              ) : (
                <Typography
                  variant="body1"
                  align="center"
                  sx={{ color: "#9ca3af", fontStyle: "italic", mt: 3 }}
                >
                  Aucune candidature trouvée.
                </Typography>
              )}
            </>
          )}
        </Box>
      </Modal>

      {/* DELETE confirmation ---------------------------------------------- */}
      <DeleteModal
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        message="Êtes‑vous sûr de vouloir supprimer cet appel d'offre ?"
      />
    </Container>
  );
}
